import { FORM_LIMITS, formFieldProtection, formRevision, looksLikeCredentialField, type FormDefinition, type FormField } from "@dayvise/contracts";

export type FormFieldValue = string | number | boolean | readonly string[];

export interface FormFieldDraft {
  readonly value: FormFieldValue;
  readonly protect: boolean;
  readonly plainConfirmed: boolean;
}

export type FormDrafts = Readonly<Record<string, FormFieldDraft>>;

export interface FormProtectedAnswer {
  readonly fieldId: string;
  readonly label: string;
  readonly value: string;
}

export interface FormSubmission {
  readonly answers: Readonly<Record<string, unknown>>;
  readonly protectedAnswers: readonly FormProtectedAnswer[];
  readonly ordinaryFieldIds: readonly string[];
  readonly formRevision: string;
}

export interface FormFieldProblem {
  readonly fieldId: string;
  readonly message: string;
}

export function formFieldValueLabel(field: FormField): string {
  if (field.type === "secret") return field.kind === "otp" ? "One-time code" : "Credential";
  if (field.type === "multichoice") return "Multiple choice";
  if (field.type === "choice" || field.type === "select") return "Choice";
  if (field.type === "number") return "Number";
  if (field.type === "boolean") return "Yes or no";
  if (field.type === "date") return "Date";
  if (field.type === "multiline") return "Long text";
  return "Text";
}

export function formFieldAutoComplete(field: FormField): string {
  if (field.type !== "secret") return "off";
  if (field.kind === "otp") return "one-time-code";
  if (field.kind === "password") return "new-password";
  return "off";
}

export function initialFormDrafts(form: FormDefinition): FormDrafts {
  const drafts: Record<string, FormFieldDraft> = {};
  for (const field of form.fields) {
    drafts[field.id] = {
      value: field.type === "multichoice" ? [] : field.type === "boolean" ? false : "",
      protect: formFieldProtection(field) === "declared-secret",
      plainConfirmed: false,
    };
  }
  return drafts;
}

export function formDraftIsEmpty(field: FormField, draft: FormFieldDraft | undefined): boolean {
  const value = draft?.value;
  if (value === undefined || value === null) return true;
  if (field.type === "multichoice") return !Array.isArray(value) || value.length === 0;
  if (field.type === "boolean") return value !== true;
  return String(value).trim().length === 0;
}

export function formDraftRequiresProtection(field: FormField, draft: FormFieldDraft | undefined): boolean {
  return formFieldProtection(field) === "declared-secret" || draft?.protect === true;
}

export function protectedFieldCount(form: FormDefinition, drafts: FormDrafts): number {
  return form.fields.filter((field) => !formDraftIsEmpty(field, drafts[field.id]) && formDraftRequiresProtection(field, drafts[field.id])).length;
}

export function formProblems(form: FormDefinition, drafts: FormDrafts): FormFieldProblem[] {
  const problems: FormFieldProblem[] = [];
  for (const field of form.fields) {
    const draft = drafts[field.id];
    const empty = formDraftIsEmpty(field, draft);
    const value = draft?.value;
    if (field.required === true && empty && field.type !== "boolean") {
      problems.push({ fieldId: field.id, message: field.type === "secret" ? "this credential is required" : "this field is required" });
      continue;
    }
    if (empty) continue;
    if (field.type === "text" || field.type === "multiline") {
      const max = field.maxLength ?? FORM_LIMITS.maxTextLength;
      if (typeof value === "string" && value.length > max) problems.push({ fieldId: field.id, message: `must be at most ${max} characters` });
      if (looksLikeCredentialField(field) && draft?.protect !== true && draft?.plainConfirmed !== true) {
        problems.push({ fieldId: field.id, message: "protect this answer, or confirm it is not a credential" });
      }
      continue;
    }
    if (field.type === "number") {
      const numeric = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(numeric)) { problems.push({ fieldId: field.id, message: "expected a number" }); continue; }
      if (field.integer === true && !Number.isInteger(numeric)) { problems.push({ fieldId: field.id, message: "expected a whole number" }); continue; }
      if (field.min !== undefined && numeric < field.min) { problems.push({ fieldId: field.id, message: `must be at least ${field.min}` }); continue; }
      if (field.max !== undefined && numeric > field.max) { problems.push({ fieldId: field.id, message: `must be at most ${field.max}` }); continue; }
      continue;
    }
    if (field.type === "multichoice") {
      const selected = Array.isArray(value) ? value : [];
      if (field.minSelected !== undefined && selected.length < field.minSelected) { problems.push({ fieldId: field.id, message: `select at least ${field.minSelected}` }); continue; }
      if (field.maxSelected !== undefined && selected.length > field.maxSelected) problems.push({ fieldId: field.id, message: `select at most ${field.maxSelected}` });
      continue;
    }
    if (field.type === "date" && typeof value === "string" && !/^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/.test(value)) {
      problems.push({ fieldId: field.id, message: "expected an unambiguous date" });
    }
  }
  return problems;
}

export function buildFormSubmission(form: FormDefinition, drafts: FormDrafts, clarificationId: string): FormSubmission {
  const answers: Record<string, unknown> = {};
  const protectedAnswers: FormProtectedAnswer[] = [];
  const ordinaryFieldIds: string[] = [];
  for (const field of form.fields) {
    const draft = drafts[field.id];
    const empty = formDraftIsEmpty(field, draft);
    if (draft?.plainConfirmed === true && looksLikeCredentialField(field) && formFieldProtection(field) !== "declared-secret") ordinaryFieldIds.push(field.id);
    if (formDraftRequiresProtection(field, draft)) {
      if (!empty) protectedAnswers.push({ fieldId: field.id, label: field.label, value: String(draft?.value ?? "") });
      continue;
    }
    if (empty) continue;
    answers[field.id] = field.type === "number" ? (typeof draft?.value === "number" ? draft.value : Number(draft?.value)) : draft?.value;
  }
  return { answers, protectedAnswers, ordinaryFieldIds, formRevision: formRevision(form, clarificationId) };
}

export function clearSensitiveDrafts(form: FormDefinition, drafts: FormDrafts): FormDrafts {
  const cleared: Record<string, FormFieldDraft> = {};
  for (const field of form.fields) {
    const draft = drafts[field.id] ?? { value: "", protect: false, plainConfirmed: false };
    const sensitive = formFieldProtection(field) !== "ordinary" || draft.protect;
    cleared[field.id] = sensitive ? { ...draft, value: field.type === "multichoice" ? [] : "" } : draft;
  }
  return cleared;
}

export function clearAllDrafts(form: FormDefinition, drafts: FormDrafts): FormDrafts {
  const cleared: Record<string, FormFieldDraft> = {};
  for (const field of form.fields) cleared[field.id] = { ...(drafts[field.id] ?? { value: "", protect: false, plainConfirmed: false }), value: field.type === "multichoice" ? [] : field.type === "boolean" ? false : "" };
  return cleared;
}

export const FORM_SNOOZE_MS = 5 * 60_000;

export function snoozeIsActive(until: number | null, now: number = Date.now()): boolean {
  return until !== null && until > now;
}

export function snoozeRemainingLabel(until: number, now: number = Date.now()): string {
  const remaining = Math.max(0, until - now);
  const minutes = Math.floor(remaining / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1000);
  return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
}
